import { editsOf, findSection, hasValue, value } from '../../parser/fortigate'
import type { FgConfig } from '../../parser/types'
import { finding, type Finding, type Rule } from '../types'

/** Admin protocols that should never be reachable from an untrusted network. */
const MGMT_PROTOCOLS = ['telnet', 'http', 'https', 'ssh', 'snmp']

export const telnetAdmin: Rule = {
  id: 'fg-mgmt-telnet',
  title: 'Telnet administrative access',
  run(config) {
    const out: Finding[] = []
    const global = findSection(config, 'system', 'global')

    if (value(global, 'admin-telnet') === 'enable') {
      out.push(
        finding(telnetAdmin, {
          severity: 'critical',
          title: 'Telnet is enabled for device administration',
          detail:
            'system global has admin-telnet set to enable. Telnet carries credentials and every ' +
            'command in cleartext, so anyone able to observe the path can capture an administrator password.',
          location: 'system global',
          line: global?.line,
          pci: '2.2.7',
          remediation: 'config system global\n    set admin-telnet disable\nend',
        }),
      )
    }

    for (const iface of editsOf(config, 'system', 'interface')) {
      if (hasValue(iface, 'allowaccess', 'telnet')) {
        out.push(
          finding(telnetAdmin, {
            severity: 'critical',
            title: `Interface ${iface.key} accepts Telnet`,
            detail:
              `allowaccess on interface ${iface.key} includes telnet, so the device answers ` +
              'unencrypted administrative logins on that interface.',
            location: `system interface ${iface.key}`,
            line: iface.line,
            pci: '2.2.7',
            remediation:
              `config system interface\n    edit "${iface.key}"\n` +
              '        set allowaccess ping https ssh\n    next\nend',
          }),
        )
      }
    }

    return out
  },
}

export const httpAdmin: Rule = {
  id: 'fg-mgmt-http',
  title: 'Unencrypted HTTP administrative access',
  run(config) {
    return editsOf(config, 'system', 'interface')
      .filter((iface) => hasValue(iface, 'allowaccess', 'http'))
      .map((iface) =>
        finding(httpAdmin, {
          severity: 'high',
          title: `Interface ${iface.key} accepts plain HTTP management`,
          detail:
            `allowaccess on interface ${iface.key} includes http. The management GUI is served ` +
            'without TLS, exposing the session cookie and any credentials sent over it.',
          location: `system interface ${iface.key}`,
          line: iface.line,
          pci: '2.2.7',
          remediation:
            `config system interface\n    edit "${iface.key}"\n` +
            '        set allowaccess ping https ssh\n    next\nend',
        }),
      )
  },
}

export const managementOnWan: Rule = {
  id: 'fg-mgmt-on-wan',
  title: 'Management plane exposed on a WAN interface',
  run(config) {
    const out: Finding[] = []

    for (const iface of editsOf(config, 'system', 'interface')) {
      if (value(iface, 'role') !== 'wan') continue

      const exposed = MGMT_PROTOCOLS.filter((p) => hasValue(iface, 'allowaccess', p))
      if (exposed.length === 0) continue

      out.push(
        finding(managementOnWan, {
          severity: 'critical',
          title: `WAN interface ${iface.key} exposes ${exposed.join(', ')}`,
          detail:
            `Interface ${iface.key} has role wan and allowaccess includes ${exposed.join(', ')}. ` +
            'The administrative interface is reachable from the untrusted side, where it will be ' +
            'found by internet-wide scanning within hours and subjected to continuous credential attacks.',
          location: `system interface ${iface.key}`,
          line: iface.line,
          pci: '1.4.2',
          remediation:
            `config system interface\n    edit "${iface.key}"\n` +
            '        set allowaccess ping\n    next\nend\n' +
            '# Reach the GUI over VPN, or restrict it with trusthosts on every admin account.',
        }),
      )
    }

    return out
  },
}

export const adminWithoutTrusthost: Rule = {
  id: 'fg-admin-no-trusthost',
  title: 'Administrator account without a source address restriction',
  run(config) {
    const out: Finding[] = []

    for (const admin of editsOf(config, 'system', 'admin')) {
      const hosts = Object.entries(admin.sets)
        .filter(([key]) => key.startsWith('trusthost'))
        .map(([, values]) => values.join(' '))

      const restricted = hosts.some((h) => h !== '' && !isAnyHost(h))
      if (restricted) continue

      out.push(
        finding(adminWithoutTrusthost, {
          severity: 'high',
          title: `Administrator "${admin.key}" can log in from any address`,
          detail:
            hosts.length === 0
              ? `No trusthost is configured for administrator "${admin.key}", so logins are accepted ` +
                'from every source the management interface is reachable from.'
              : `Administrator "${admin.key}" has a trusthost of 0.0.0.0/0, which permits every source ` +
                'address and is equivalent to setting no restriction at all.',
          location: `system admin ${admin.key}`,
          line: admin.line,
          // No PCI reference: restricting admin source addresses is covered in spirit by
          // Requirement 7, but the sub-requirement mapping is not one I have verified
          // against the standard, and a wrong citation is worse than none.
          remediation:
            `config system admin\n    edit "${admin.key}"\n` +
            '        set trusthost1 10.0.0.0 255.255.255.0\n    next\nend',
        }),
      )
    }

    return out
  },
}

export const defaultAdminAccount: Rule = {
  id: 'fg-admin-default-name',
  title: 'Default administrator account name in use',
  run(config) {
    return editsOf(config, 'system', 'admin')
      .filter((admin) => admin.key.toLowerCase() === 'admin')
      .map((admin) =>
        finding(defaultAdminAccount, {
          severity: 'medium',
          title: 'The default "admin" account is still present',
          detail:
            'An account named admin exists. Credential-stuffing and brute-force tooling targets this ' +
            'name first, so keeping it hands an attacker half of every login attempt for free.',
          location: 'system admin admin',
          line: admin.line,
          pci: '2.2.2',
          remediation:
            'config system admin\n    edit "your-named-account"\n' +
            '        set accprofile "super_admin"\n        set password <secret>\n    next\nend\n' +
            '# Verify the new account works, then: delete admin',
        }),
      )
  },
}

export const longAdminTimeout: Rule = {
  id: 'fg-admin-timeout',
  title: 'Idle administrative session timeout too long',
  run(config) {
    const global = findSection(config, 'system', 'global')
    const raw = value(global, 'admintimeout')
    if (raw === undefined) return [] // FortiOS defaults to 5 minutes, which is fine.

    const minutes = Number(raw)
    if (!Number.isFinite(minutes) || minutes <= 15) return []

    return [
      finding(longAdminTimeout, {
        severity: 'medium',
        title: `Administrative sessions stay open for ${minutes} minutes when idle`,
        detail:
          `system global sets admintimeout to ${minutes}. An unattended browser leaves an authenticated ` +
          'session usable by anyone with access to that machine for the whole window.',
        location: 'system global',
        line: global?.line,
        pci: '8.2.8',
        remediation: 'config system global\n    set admintimeout 5\nend',
      }),
    ]
  },
}

export const weakAdminTls: Rule = {
  id: 'fg-mgmt-weak-tls',
  title: 'Deprecated TLS versions accepted by the management GUI',
  run(config) {
    const global = findSection(config, 'system', 'global')
    const deprecated = ['tlsv1-0', 'tlsv1-1', 'sslv3']
    const enabled = deprecated.filter((v) => hasValue(global, 'admin-https-ssl-versions', v))
    if (enabled.length === 0) return []

    return [
      finding(weakAdminTls, {
        severity: 'high',
        title: `Management GUI still accepts ${enabled.join(', ')}`,
        detail:
          `admin-https-ssl-versions includes ${enabled.join(', ')}. These versions have known ` +
          'weaknesses and allow a downgrade of the administrative session.',
        location: 'system global',
        line: global?.line,
        pci: '2.2.7',
        remediation:
          'config system global\n    set admin-https-ssl-versions tlsv1-2 tlsv1-3\n    set strong-crypto enable\nend',
      }),
    ]
  },
}

export const strongCryptoDisabled: Rule = {
  id: 'fg-strong-crypto',
  title: 'strong-crypto not enabled',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (global === undefined) return []
    if (value(global, 'strong-crypto') === 'enable') return []

    return [
      finding(strongCryptoDisabled, {
        severity: 'medium',
        title: 'strong-crypto is not enabled',
        detail:
          'system global does not set strong-crypto to enable, so the device may negotiate legacy ' +
          'ciphers and hashes for its own SSH and TLS services.',
        location: 'system global',
        line: global.line,
        pci: '2.2.7',
        remediation: 'config system global\n    set strong-crypto enable\nend',
      }),
    ]
  },
}

function isAnyHost(host: string): boolean {
  const normalised = host.trim()
  return (
    normalised === '0.0.0.0 0.0.0.0' ||
    normalised === '0.0.0.0/0' ||
    normalised === '0.0.0.0' ||
    normalised === '::/0'
  )
}

export const MANAGEMENT_RULES: Rule[] = [
  telnetAdmin,
  httpAdmin,
  managementOnWan,
  adminWithoutTrusthost,
  defaultAdminAccount,
  longAdminTimeout,
  weakAdminTls,
  strongCryptoDisabled,
]

export type { FgConfig }
